import { useCallback, useEffect, useState, type RefCallback } from 'react';

type UseResizeObserverOptions = {
  /**
   * Which box to measure. `content-box` (the default) leaves out padding and
   * border. `border-box` includes them, matching `getBoundingClientRect`, for
   * sizing something to an element's on-screen width.
   */
  box?: 'content-box' | 'border-box';
};

/**
 * Observes an element's dimensions via ResizeObserver.
 * Returns a callback ref to attach to the target element, plus the
 * measured width/height. Re-observes automatically when the element changes
 * (e.g. due to conditional rendering or list reordering).
 */
export function useResizeObserver({ box = 'content-box' }: UseResizeObserverOptions = {}): [
  RefCallback<Element>,
  { width: number | undefined; height: number | undefined },
] {
  const [element, setElement] = useState<Element | null>(null);
  const [width, setWidth] = useState<number | undefined>();
  const [height, setHeight] = useState<number | undefined>();

  const ref = useCallback((node: Element | null) => {
    setElement(node);
  }, []);

  useEffect(() => {
    if (element == null) {
      setWidth(undefined);
      setHeight(undefined);
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      const borderBox = box === 'border-box' ? entry.borderBoxSize?.[0] : undefined;
      setWidth(borderBox != null ? borderBox.inlineSize : entry.contentRect.width);
      setHeight(borderBox != null ? borderBox.blockSize : entry.contentRect.height);
    });

    observer.observe(element, { box });
    return () => observer.disconnect();
  }, [element, box]);

  return [ref, { width, height }];
}
